import React from 'react';

export const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:3001/api';

// Helper to render <option> tags from dropdownOptions
export function renderOptions(options, placeholder) {
  return (
    <>
      {placeholder && <option value="">{placeholder}</option>}
      {(options || []).map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </>
  );
}

// Helper: determine if an entity record is "active"
export function isEntityActive(type, record) {
  switch (type) {
    case 'contact':
      return record.Status !== 'Inactive';
    case 'property':
      return record.Status !== 'Inactive';
    case 'lead':
      return !['Closed-Won', 'Closed-Lost'].includes(record.Status);
    case 'contract':
      return !['Completed', 'Terminated', 'Expired'].includes(record.Status);
    case 'reminder':
      return record.Status !== 'Completed';
    default:
      return true;
  }
}

// Status filter toggle: Show All / Show Active / Show Inactive
export function StatusFilter({ value, onChange }) {
  const options = ['Active', 'All', 'Inactive'];
  return (
    <div className="crm-status-filter">
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`crm-status-filter__btn ${value === opt ? 'crm-status-filter__btn--active' : ''}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// Tiered money formatter: $850 / $45K / $3.5M
export function formatMoney(raw) {
  const value = Number(raw) || 0;
  if (value >= 1000000) {
    const m = value / 1000000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

export function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Multi-assignee picker (checkbox list). `value` is an array of user IDs;
// `onChange` receives the updated array. Reused by the reminder forms.
export function AssigneeMultiSelect({ users, value, onChange }) {
  const selected = new Set((value || []).map(Number));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const list = users || [];
  if (list.length === 0) {
    return <p className="crm-text--muted" style={{ fontSize: '0.813rem', margin: 0 }}>No users available</p>;
  }
  return (
    <div
      className="crm-assignee-select"
      style={{ maxHeight: '132px', overflowY: 'auto', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '0.35rem 0.6rem' }}
    >
      {list.map(u => (
        <label key={u.UserId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.15rem 0', cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={selected.has(Number(u.UserId))}
            onChange={() => toggle(Number(u.UserId))}
          />
          {u.FirstName} {u.LastName}
        </label>
      ))}
    </div>
  );
}

// Parse a reminder's Assignees column (JSON string from SQL Server FOR JSON, or
// an array) into [{ UserId, Name }, ...].
export function parseAssignees(reminder) {
  if (!reminder) return [];
  const raw = reminder.Assignees;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try { return JSON.parse(raw) || []; } catch { return []; }
  }
  return [];
}

// Parse a lead's LinkedProperties column (JSON string from SQL Server FOR JSON,
// or an array) into [{ PropertyId, PropertyName }, ...].
export function parseLinkedProperties(lead) {
  if (!lead) return [];
  const raw = lead.LinkedProperties;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try { return JSON.parse(raw) || []; } catch { return []; }
  }
  return [];
}
